import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { foldersApi } from '@/api/foldersApi'
import { scansApi } from '@/api/scansApi'
import { exportApi } from '@/api/exportApi'
import { toApiError, type ApiError } from '@/api/httpClient'
import { downloadBlob } from '@/utilities/download'
import type { FolderContents, ScanSortMode } from '@/models/library'

export const useLibraryStore = defineStore('library', () => {
  const currentFolderId = ref<string | null>(null)
  const contents = ref<FolderContents | null>(null)
  const loading = ref(false)
  const busy = ref(false)
  const error = ref<ApiError | null>(null)
  const selected = ref<Set<string>>(new Set())

  // Per-folder sort preference. Persisted on the server (folder.sortMode); this ref
  // mirrors the current folder's value and is refreshed on load.
  const sortMode = ref<ScanSortMode>('Manual')

  // Server order (manual/drag order) as stored.
  const rawScans = computed(() => contents.value?.scans ?? [])

  // Display order. Sorting is applied on top of the stored order; combining a PDF uses
  // this same order, so pages come out sorted by date when requested.
  // Sort key is createdUtc = the immutable capture/upload time. Cropping/rotating a scan
  // bumps updatedUtc only, so editing never reorders a document out of its sequence.
  // Array.sort is stable, so scans with an identical timestamp keep their upload order.
  const scans = computed(() => {
    if (sortMode.value === 'Manual') return rawScans.value
    const dir = sortMode.value === 'DateAsc' ? 1 : -1
    return [...rawScans.value].sort(
      (a, b) => (Date.parse(a.createdUtc) - Date.parse(b.createdUtc)) * dir
    )
  })

  /** Persists the current folder's sort preference on the server (optimistic). */
  async function setSortMode(mode: ScanSortMode): Promise<boolean> {
    const folderId = currentFolderId.value
    if (folderId === null) {
      sortMode.value = mode
      return true
    }
    const previous = sortMode.value
    sortMode.value = mode // optimistic
    return run(async () => {
      const updated = await foldersApi.setSort(folderId, mode)
      // Keep the folder DTO in contents in sync so it survives a refresh.
      if (contents.value?.folder && contents.value.folder.id === folderId) {
        contents.value.folder.sortMode = updated.sortMode
      }
      sortMode.value = updated.sortMode
    }).then((ok) => {
      if (!ok) sortMode.value = previous // revert on failure
      return ok
    })
  }

  const subFolders = computed(() => contents.value?.subFolders ?? [])
  const breadcrumb = computed(() => contents.value?.breadcrumb ?? [])
  const currentFolder = computed(() => contents.value?.folder ?? null)
  const atRoot = computed(() => currentFolderId.value === null)

  const selectedCount = computed(() => selected.value.size)
  const selectedImageIds = computed(() =>
    scans.value.filter((s) => selected.value.has(s.id) && s.kind === 'Image').map((s) => s.id)
  )

  function isSelected(id: string): boolean {
    return selected.value.has(id)
  }

  function toggleSelect(id: string): void {
    const next = new Set(selected.value)
    next.has(id) ? next.delete(id) : next.add(id)
    selected.value = next
  }

  function clearSelection(): void {
    selected.value = new Set()
  }

  function selectAll(): void {
    selected.value = new Set(scans.value.map((s) => s.id))
  }

  async function load(folderId: string | null): Promise<void> {
    loading.value = true
    error.value = null
    clearSelection()
    try {
      currentFolderId.value = folderId
      contents.value = await foldersApi.contents(folderId)
      // Adopt this folder's persisted sort preference (root has no folder → Manual).
      sortMode.value = contents.value.folder?.sortMode ?? 'Manual'
    } catch (err) {
      error.value = toApiError(err)
    } finally {
      loading.value = false
    }
  }

  async function refresh(): Promise<void> {
    await load(currentFolderId.value)
  }

  async function createFolder(name: string): Promise<boolean> {
    return run(async () => {
      await foldersApi.create({ name, parentId: currentFolderId.value })
      await refresh()
    })
  }

  async function renameFolder(id: string, name: string): Promise<boolean> {
    return run(async () => {
      await foldersApi.rename(id, name)
      await refresh()
    })
  }

  async function deleteFolder(id: string): Promise<boolean> {
    return run(async () => {
      await foldersApi.remove(id)
      await refresh()
    })
  }

  async function uploadFiles(files: File[]): Promise<boolean> {
    if (currentFolderId.value === null) {
      error.value = { status: null, title: 'Open a folder before uploading scans.' }
      return false
    }
    return run(async () => {
      await scansApi.upload(currentFolderId.value as string, files)
      await refresh()
    })
  }

  async function renameScan(id: string, name: string): Promise<boolean> {
    return run(async () => {
      await scansApi.rename(id, name)
      await refresh()
    })
  }

  async function deleteScan(id: string): Promise<boolean> {
    return run(async () => {
      await scansApi.remove(id)
      await refresh()
    })
  }

  async function deleteSelected(): Promise<boolean> {
    return run(async () => {
      for (const id of Array.from(selected.value)) {
        await scansApi.remove(id)
      }
      await refresh()
    })
  }

  async function combineSelected(name?: string): Promise<boolean> {
    if (currentFolderId.value === null || selectedImageIds.value.length === 0) {
      error.value = { status: null, title: 'Select at least one image to combine.' }
      return false
    }
    return run(async () => {
      await scansApi.combine(currentFolderId.value as string, {
        scanIds: selectedImageIds.value,
        name
      })
      await refresh()
    })
  }

  async function zipSelected(): Promise<boolean> {
    const ids = Array.from(selected.value)
    return run(async () => {
      const { blob, fileName } = await exportApi.zip({ scanIds: ids })
      downloadBlob(blob, fileName)
    })
  }

  async function zipCurrentFolder(): Promise<boolean> {
    if (currentFolderId.value === null) {
      error.value = { status: null, title: 'Open a folder to zip its scans.' }
      return false
    }
    return run(async () => {
      const { blob, fileName } = await exportApi.zip({ folderId: currentFolderId.value })
      downloadBlob(blob, fileName)
    })
  }

  /** Runs an action with shared busy/error handling. Returns success. */
  async function run(action: () => Promise<void>): Promise<boolean> {
    busy.value = true
    error.value = null
    try {
      await action()
      return true
    } catch (err) {
      error.value = toApiError(err)
      return false
    } finally {
      busy.value = false
    }
  }

  return {
    currentFolderId,
    contents,
    loading,
    busy,
    error,
    selected,
    sortMode,
    setSortMode,
    scans,
    subFolders,
    breadcrumb,
    currentFolder,
    atRoot,
    selectedCount,
    selectedImageIds,
    isSelected,
    toggleSelect,
    clearSelection,
    selectAll,
    load,
    refresh,
    createFolder,
    renameFolder,
    deleteFolder,
    uploadFiles,
    renameScan,
    deleteScan,
    deleteSelected,
    combineSelected,
    zipSelected,
    zipCurrentFolder
  }
})
